import { useState, useEffect, createRef } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { FormInput, FormDropDownListFilter, FormDatePicker } from "../FormComponents";
import { requiredValidator } from "../Validators";
import { UpdateTool, Tool, fileDocumentTypes } from "../../../data/interfaces";
import { getEmployees } from "../../../data/employeeData";
import { getToolById, getToolDocuments } from "../../../data/toolData";
import SkeletonForm from "../../Skeletons/SkeletonForm";
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from '@progress/kendo-react-layout';
import {
  Upload,
  UploadOnStatusChangeEvent,
  UploadFileInfo,
  UploadListItemProps,
  ExternalDropZone,
} from '@progress/kendo-react-upload';
import QuoteDocumentRow from '../../QuoteDocumentRow';
import Notes from '../../../pages/Notes/Notes';
import { addNoteDTO } from '../../../data/notes';
import ToolCosts from './ToolCosts';

interface EditToolFormProps {
  onEditSubmit: (tool: UpdateTool, toolId: number) => void;
  onToolId: number;
  onToolDelete: (toolId: number) => void;
  /** False for Tools.View: the form only shows the tool. */
  canEdit?: boolean;
}

const EditToolForm = ({ onEditSubmit, onToolId, onToolDelete, canEdit = true }: EditToolFormProps) => {
  const readOnly = !canEdit;
  const [employees, setEmployees] = useState<any[]>([]);
  const [toolData, setToolData] = useState<Tool | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const [documentData, setDocumentData] = useState<fileDocumentTypes[]>([]);

  const token = localStorage.getItem('authToken');
  const storedApiURL = localStorage.getItem('apiURL');
  // Fall back to the build-time API URL (as the rest of the app does) before
  // localhost — otherwise production uploads post to https://localhost:7048.
  const apiURL = storedApiURL?.replace('https://', '').replace('http://', '') || process.env.REACT_APP_API_URL || 'localhost:7048';

  const uploadRef = createRef<Upload>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const employeeData = await getEmployees();
        const formattedEmployees = employeeData
          .map((emp: any) => ({
            value: emp.employeeId,
            text: emp.employeeName,
          }))
          .sort((a: any, b: any) => a.text.localeCompare(b.text));
        setEmployees(formattedEmployees);

        if (onToolId && onToolId > 0) {
          const tool = await getToolById(onToolId);
          setToolData(tool);
          await fetchDocuments();
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [onToolId]);

  const handleSubmit = (dataItem: any) => {
    const setTimeTo3AM = (date: Date | null) => {
      if (!date) return null;
      const newDate = new Date(date);
      newDate.setHours(3, 0, 0, 0);
      return newDate.toISOString();
    };

    const formattedData: UpdateTool = {
      toolId: onToolId,
      make: dataItem.make || null,
      model: dataItem.model || null,
      serialNo: dataItem.serialNo || null,
      lastCalibrated: setTimeTo3AM(dataItem.lastCalibrated),
      nextCalibration: setTimeTo3AM(dataItem.nextCalibration),
      employeeId: dataItem.employeeId?.value || null,
    };
    onEditSubmit(formattedData, onToolId);
  };

  const handleDelete = () => {
    setShowDeleteDialog(false);
    onToolDelete(onToolId);
  };

  const handleSelectedTab = (e: TabStripSelectEventArguments) => {
    setSelectedTab(e.selected);
  };

  const handleUploadSuccess = (event: UploadOnStatusChangeEvent) => {
    setFiles(event.newState);
    fetchDocuments();
  };

  const fetchDocuments = async () => {
    try {
      const documents = await getToolDocuments(onToolId);
      setDocumentData(documents);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const handleDocumentDelete = async (documentId: number) => {
    await fetchDocuments();
  };

  const addNote: addNoteDTO = {
    enquiryId: null,
    quoteId: null,
    jobId: null,
    vehicleId: null,
    toolId: onToolId,
  };

  if (loading || employees.length === 0) {
    return <SkeletonForm />;
  }

  if (!onToolId || onToolId === 0) {
    return <div>No tool selected for editing</div>;
  }

  if (!toolData) {
    return <div>Tool not found (ID: {onToolId})</div>;
  }

  const selectedEmployee = toolData.employeeId
    ? employees.find((emp) => emp.value === toolData.employeeId)
    : null;

  const parseDate = (dateString: any) => {
    if (!dateString || dateString === "" || dateString === "null") return null;
    if (dateString instanceof Date) return dateString;

    let date: Date;
    if (typeof dateString === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        date = new Date(dateString + 'T12:00:00');
      } else {
        date = new Date(dateString);
      }
    } else {
      date = new Date(dateString);
    }

    return isNaN(date.getTime()) ? null : date;
  };

  const initialValues = {
    make: toolData.make || "",
    model: toolData.model || "",
    serialNo: (toolData as any).serialNo || "",
    lastCalibrated: parseDate(toolData.lastCalibrated),
    nextCalibration: parseDate((toolData as any).nextCalibration),
    employeeId: selectedEmployee,
  };

  return (
    <div className="form__wrapper">
      {showDeleteDialog && (
        <Dialog title="Confirm Delete" onClose={() => setShowDeleteDialog(false)}>
          <p>Are you sure you want to delete this tool?</p>
          <DialogActionsBar>
            <button className="form__edit__button reject" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </button>
            <button className="form__edit__button accept" onClick={handleDelete}>
              Delete
            </button>
          </DialogActionsBar>
        </Dialog>
      )}

      <div style={{ display: 'flex', width: '100%' }}>
        <TabStrip
          selected={selectedTab}
          onSelect={handleSelectedTab}
          className='form__tabstrip__container'
          animation={false}
        >
          <TabStripTab title='Tool Form'>
            <Form
              key={`tool-form-${onToolId}`}
              onSubmit={handleSubmit}
              initialValues={initialValues}
              render={(formRenderProps) => (
                <FormElement style={{ padding: '1rem', width: '100%', marginBottom: '5rem' }}>
                  <fieldset className="k-form-fieldset" disabled={readOnly}>
                    <legend className="k-form-legend">Tool Details</legend>
                    {readOnly && <p className='k-form-hint'>You have view-only access to tools.</p>}

                    <Field
                      name="make"
                      component={FormInput}
                      label="Make *"
                      validator={requiredValidator}
                      disabled={readOnly}
                    />

                    <Field
                      name="model"
                      component={FormInput}
                      label="Model"
                      disabled={readOnly}
                    />

                    <Field
                      name="serialNo"
                      component={FormInput}
                      label="Serial No"
                      disabled={readOnly}
                    />

                    <Field
                      name="lastCalibrated"
                      component={FormDatePicker}
                      label="Last Calibrated"
                      disabled={readOnly}
                    />

                    <Field
                      name="nextCalibration"
                      component={FormDatePicker}
                      label="Next Calibration"
                      disabled={readOnly}
                    />

                    <Field
                      name="employeeId"
                      component={FormDropDownListFilter}
                      label="Assigned Employee"
                      data={employees}
                      textField="text"
                      dataItemKey="value"
                      disabled={readOnly}
                    />
                  </fieldset>

                  <div className="form__info" style={{ marginTop: "1rem", fontSize: "0.9rem", color: "#666" }}>
                    <p><strong>Created:</strong> {toolData.createDate ? new Date(toolData.createDate).toLocaleDateString() : 'N/A'} by {toolData.employeeCreatedbyName || 'N/A'}</p>
                    <p><strong>Modified:</strong> {toolData.modifiedDate ? new Date(toolData.modifiedDate).toLocaleDateString() : 'N/A'} by {toolData.employeeModifiedbyName || 'N/A'}</p>
                  </div>

                  {!readOnly && (
                  <div className='button__container'>
                    <button
                      type="submit"
                      disabled={!formRenderProps.allowSubmit}
                      className='form__edit__button accept'
                    >
                      Update Tool
                    </button>
                    <button
                      type="button"
                      className='form__edit__button reject'
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      Delete Tool
                    </button>
                  </div>
                  )}
                </FormElement>
              )}
            />
          </TabStripTab>

          <TabStripTab title='Files'>
            <div style={{ padding: '1rem' }}>
              {!readOnly && (
              <>
              <ExternalDropZone uploadRef={uploadRef} />
              <Upload
                ref={uploadRef}
                batch={false}
                multiple={true}
                withCredentials={false}
                saveUrl={`https://${apiURL}/Files/Tool/${onToolId}`}
                saveHeaders={{
                  Authorization: `Bearer ${token}`,
                }}
                onStatusChange={handleUploadSuccess}
                files={files}
                onAdd={(e) => setFiles(e.newState)}
                onRemove={(e) => setFiles(e.newState)}
              />
              </>
              )}
              <div style={{ marginTop: '1rem' }}>
                <h4>Uploaded Documents</h4>
                {documentData && documentData.length > 0 ? (
                  documentData.map((doc: any) => (
                    <QuoteDocumentRow
                      key={doc.documentId}
                      onDocumentId={doc.documentId}
                      onFileName={doc.fileName}
                      onFileExtension={doc.fileExtension}
                      appCanView={doc.appCanView}
                      onDocumentDelete={handleDocumentDelete}
                      showVisibilityToggle={false}
                      readOnly={readOnly}
                    />
                  ))
                ) : (
                  <p style={{ color: '#666' }}>No documents uploaded yet.</p>
                )}
              </div>
            </div>
          </TabStripTab>

          <TabStripTab title='Notes'>
            <Notes sourceEntity={addNote} lockEdit={readOnly} />
          </TabStripTab>

          <TabStripTab title='Costs'>
            <ToolCosts toolId={onToolId} canEdit={canEdit} />
          </TabStripTab>
        </TabStrip>
      </div>
    </div>
  );
};

export default EditToolForm;
